/**
 * Client-side Authentication Module
 *
 * Handles user authentication state and UI.
 */

import { ClientLogger } from './logger/ClientLogger.js';

const authLogger = ClientLogger.getLogger('auth') ||
  console;

// Current user state
let currentUser = null;
let isAuthenticated = false;

// Google icon SVG
const googleIconSVG = `<svg viewBox="0 0 24 24" fill="currentColor">
  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
</svg>`;

/**
 * Initialize authentication
 */
async function init() {
  await checkAuthStatus();
  updateAuthUI();
}

/**
 * Check authentication status with server
 */
async function checkAuthStatus() {
  try {
    const response = await fetch('/auth/me');
    if (!response.ok) {
      // Auth routes are not mounted when Google OAuth is not configured; that is
      // a guest session, not an error.
      isAuthenticated = false;
      currentUser = null;
      return { authenticated: false, user: null };
    }
    const data = await response.json();

    isAuthenticated = data.authenticated;
    currentUser = data.user;

    return data;
  } catch (error) {
    authLogger.error('[Auth] Auth check failed', { error });
    isAuthenticated = false;
    currentUser = null;
    return { authenticated: false, user: null };
  }
}

/**
 * Update the auth section UI based on current state
 */
function updateAuthUI() {
  const authSection = document.getElementById('authSection');
  const playerNameInput = document.getElementById('playerNameInput');

  if (!authSection) return;

  if (isAuthenticated && currentUser) {
    // User is logged in
    const avatarHtml = currentUser.avatarUrl
      ? `<img src="${currentUser.avatarUrl}" alt="Avatar" class="user-avatar">`
      : '';

    authSection.innerHTML = `
      <div class="user-info">
        ${avatarHtml}
        <span class="user-name">${escapeHtml(currentUser.displayName)}</span>
        <button class="logout-btn" onclick="Auth.logout()">Logout</button>
      </div>
      <div class="guest-notice">Stats will be saved to your account</div>
    `;

    // Set player name to user's display name
    if (playerNameInput) {
      playerNameInput.value = currentUser.displayName;
    }
  } else {
    // User is not logged in
    authSection.innerHTML = `
      <button class="google-btn" onclick="Auth.loginWithGoogle()">
        ${googleIconSVG}
        Sign in with Google
      </button>
      <div class="guest-notice">Sign in to save your stats</div>
    `;
  }
}

/**
 * Initiate Google OAuth login
 */
function loginWithGoogle() {
  window.location.href = '/auth/google';
}

/**
 * Logout current user
 */
async function logout() {
  try {
    await fetch('/auth/logout', { method: 'POST' });
    isAuthenticated = false;
    currentUser = null;
    updateAuthUI();

    // Reset player name input
    const playerNameInput = document.getElementById('playerNameInput');
    if (playerNameInput) {
      playerNameInput.value = 'Player';
    }
  } catch (error) {
    authLogger.error('[Auth] Logout failed', { error });
  }
}

/**
 * Get current user
 * @returns {Object|null}
 */
function getUser() {
  return currentUser;
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
function isLoggedIn() {
  return isAuthenticated;
}

/**
 * Get user ID (for sending to server with game events)
 * @returns {string|null}
 */
function getUserId() {
  return currentUser?.id || null;
}

/**
 * Helper to escape HTML
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Module API (main.js exposes it on window for console use)
const Auth = {
  init,
  checkAuthStatus,
  updateAuthUI,
  loginWithGoogle,
  logout,
  getUser,
  isLoggedIn,
  getUserId
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { authLogger, currentUser, isAuthenticated, googleIconSVG, init, checkAuthStatus, updateAuthUI, loginWithGoogle, logout, getUser, isLoggedIn, getUserId, escapeHtml };

export { Auth };
